import React, { useState, useEffect, useRef } from 'react';
import { X, ZoomIn, ZoomOut, ChevronLeft } from 'lucide-react';
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Import pdfjs from react-pdf
import { pdfjs } from 'react-pdf';
import { apiClient, DATA_API_URL } from '../lib/api';

// Use the worker file copied from react-pdf's bundled pdfjs-dist (v5.4.296)
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

export default function PDFViewerWithHighlights({ documentId, onClose, documentData = null, source = 'live' }) {
  const [docData, setDocData] = useState(null);
  const [citations, setCitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeClaim, setActiveClaim] = useState(null);
  const [htmlContent, setHtmlContent] = useState(null);
  const [htmlLoading, setHtmlLoading] = useState(false);

  // PDF viewer state
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1.3); // Increased default scale for better readability
  const [searchText, setSearchText] = useState('');
  const [activeHighlightIndex, setActiveHighlightIndex] = useState(null);
  const [allHighlights, setAllHighlights] = useState([]); // Track ALL citation highlights

  // Refs for scrolling
  const pdfContainerRef = useRef(null);
  const pdfScrollContainerRef = useRef(null);
  const pageRefs = useRef({});
  const htmlContentRef = useRef(null);
  const highlightTimeoutRef = useRef(null);
  const mutationObserverRef = useRef(null);
  const reapplyIntervalRef = useRef(null); // Interval for periodic re-application

  useEffect(() => {
    // documentId can legitimately be 0, so check against nullish instead of falsy
    if (documentData) {
      console.log('🔍 Using provided documentData for viewer');
      hydrateDocument(documentData);
      return;
    }
    if (documentId !== null && documentId !== undefined) {
      loadDocumentFromBackend();
    }
  }, [documentId, documentData, source]);

  useEffect(() => {
    if (docData) {
      console.log('🔍 PDF viewer hydrated docData', {
        pdf_url: docData.pdf_url,
        s3_url: docData.s3_url,
        url: docData.url,
        citationsCount: citations.length,
      });
    }
  }, [docData, citations.length]);

  // Re-apply ALL highlights when page count or scale changes
  useEffect(() => {
    if (numPages && allHighlights.length > 0) {
      console.log('🔄 Re-applying all highlights after page change/zoom');
      // Small delay to let pages render
      setTimeout(() => {
        allHighlights.forEach(({ searchText, citationIndex }) => {
          applyHighlightToText(searchText, citationIndex);
        });
      }, 300);
    }
  }, [numPages, scale, allHighlights]); // Re-highlight when these change

  // Periodically re-apply ALL highlights to combat text layer re-renders
  useEffect(() => {
    if (!numPages || allHighlights.length === 0) {
      // Clear interval if no highlights
      if (reapplyIntervalRef.current) {
        clearInterval(reapplyIntervalRef.current);
        reapplyIntervalRef.current = null;
      }
      return;
    }

    console.log(`📌 Setting up periodic re-application for ${allHighlights.length} highlights`);

    // Re-apply all highlights every 500ms to ensure they persist
    const intervalId = setInterval(() => {
      console.log('🔄 Periodic re-application of all highlights');
      allHighlights.forEach(({ searchText, citationIndex }) => {
        applyHighlightToText(searchText, citationIndex);
      });
    }, 500);

    reapplyIntervalRef.current = intervalId;

    return () => {
      if (reapplyIntervalRef.current) {
        clearInterval(reapplyIntervalRef.current);
      }
    };
  }, [allHighlights, numPages]); // Re-run when highlights list changes

  const resolvePdfUrl = (doc) => {
    // Try the most explicit PDF locations first, then fall back to any known URL
    const pdfUrl =
      doc.document_info?.pdf_url ||
      doc.pdf_url ||
      doc.s3_url ||
      doc.document_url ||
      doc.file_path ||
      doc.url;
    return pdfUrl || null;
  };

  const resolveHtmlUrl = (doc) => {
    if (doc.html_url) return doc.html_url;
    if (doc.url && !/\.pdf(\?|$)/i.test(doc.url)) return doc.url;
    return null;
  };

  const transformCitations = (doc) => {
    const src =
      doc?.source_citations ||
      doc?.simplified_analysis?.source_citations ||
      doc?.citations ||
      doc?.ai_analysis?.citations ||
      [];
    if (!Array.isArray(src)) return [];
    return src.map((c, idx) => ({
      id: c.id ?? idx,
      claim: c.claim || '',
      supporting_text: c.supporting_text || c.text || '',
      context: c.context || '',
      page_number: c.page_number || c.page || 1,
      confidence_score: c.confidence_score ?? c.confidence ?? 0,
      category: c.category || c.claim_type || '',
    }));
  };

  const hydrateDocument = (doc) => {
    const pdfUrl = resolvePdfUrl(doc);
    const htmlUrl = resolveHtmlUrl(doc);
    setDocData({ ...doc, pdf_url: pdfUrl, html_url: htmlUrl });
    const existingCitations = transformCitations(doc);
    if (existingCitations.length > 0) {
      setCitations(existingCitations);
    }
    setLoading(false);
  };

  async function fetchLiveDocument(id) {
    // Prefer direct change endpoint if available
    try {
      const direct = await fetch(`${DATA_API_URL}/api/changes/${id}`);
      if (direct.ok) return await direct.json();
    } catch (err) {
      console.warn('Direct change fetch failed, falling back to recent list', err);
    }

    const docResponse = await fetch(`${DATA_API_URL}/api/changes/recent?limit=200`);
    if (!docResponse.ok) {
      throw new Error('Failed to fetch documents');
    }
    const allDocs = await docResponse.json();
    const doc = allDocs.find(d => d.document_id === id || d.id === id);
    if (!doc) {
      throw new Error(`Document ${id} not found`);
    }
    return doc;
  }

  async function fetchArchiveDocument(id) {
    return await apiClient.getArchivedChange(id);
  }

  async function loadDocumentFromBackend() {
    try {
      setLoading(true);
      setError(null);
      
      console.log(`📡 Fetching document ${documentId} from ${source} backend...`);
      const doc = source === 'archive'
        ? await fetchArchiveDocument(documentId)
        : await fetchLiveDocument(documentId);
      
      console.log(`✅ Loaded document:`, doc.title);
      console.log(`🔍 Full document object:`, doc);
      console.log(`🔍 document_info:`, doc.document_info);
      console.log(`🔍 document_info.pdf_url:`, doc.document_info?.pdf_url);
      console.log(`🔍 doc.pdf_url:`, doc.pdf_url);
      console.log(`🔍 doc.s3_url:`, doc.s3_url);
      console.log(`🔍 doc.document_url:`, doc.document_url);
      console.log(`🔍 doc.url:`, doc.url);

      const pdfUrl = resolvePdfUrl(doc);
      const htmlUrl = resolveHtmlUrl(doc);

      if (!pdfUrl && !htmlUrl) {
        console.warn(`⚠️ WARNING: No PDF or HTML link detected for this document.`);
      }

      setDocData({ ...doc, pdf_url: pdfUrl, html_url: htmlUrl });
      const existingCitations = transformCitations(doc);
      if (existingCitations.length > 0) {
        console.log(`✅ Using ${existingCitations.length} citations from document data`);
        setCitations(existingCitations);
      }

      // Preload HTML content so we can highlight/search like PDF
      if (!pdfUrl && htmlUrl) {
        try {
          setHtmlLoading(true);
          const htmlResp = await fetch(htmlUrl);
          if (htmlResp.ok) {
            const html = await htmlResp.text();
            setHtmlContent(html);
          } else {
            console.warn(`⚠️ Failed to fetch HTML content for embedding: ${htmlResp.status}`);
            setHtmlContent(null);
          }
        } catch (htmlErr) {
          console.warn('⚠️ Error fetching HTML for embedding', htmlErr);
          setHtmlContent(null);
        } finally {
          setHtmlLoading(false);
        }
      } else {
        setHtmlContent(null);
      }
      
      if (source !== 'archive' && existingCitations.length === 0 && citations.length === 0) {
        try {
          console.log(`📋 Fetching citations from /api/changes/${documentId}/citations...`);
          const citResponse = await fetch(`${DATA_API_URL}/api/changes/${documentId}/citations`);
          
          if (citResponse.ok) {
            const cits = await citResponse.json();
            console.log(`✅ Loaded ${cits.length} citations from endpoint`);
            setCitations(cits);
          } else {
            throw new Error('Citations endpoint not available');
          }
        } catch (citError) {
          console.log(`⚠️ Citations endpoint failed, using embedded citations`);
          const embeddedCitations = transformCitations(doc);
          console.log(`✅ Using ${embeddedCitations.length} embedded citations`);
          setCitations(embeddedCitations);
        }
      }
      
      setLoading(false);
    } catch (err) {
      console.error('❌ Failed to load document:', err);
      setError(err.message);
      setLoading(false);
    }
  }

  function getHighlightColor(index) {
    const colors = [
      'rgba(255, 255, 102, 0.5)',  // Mellow yellow - semi-transparent
      'rgba(255, 235, 153, 0.5)',  // Light yellow
      'rgba(255, 248, 179, 0.5)',  // Pale yellow
      'rgba(255, 243, 128, 0.5)',  // Soft yellow
      'rgba(255, 251, 153, 0.5)',  // Cream yellow
      'rgba(254, 240, 138, 0.5)',  // Warm yellow
    ];
    return colors[index % colors.length];
  }

  // PDF document load handlers
  function onDocumentLoadSuccess({ numPages }) {
    setNumPages(numPages);
    console.log(`📄 PDF loaded: ${numPages} pages`);
  }

  function onDocumentLoadError(error) {
    console.error('Error loading PDF:', error);
    setError('Failed to load PDF document');
  }

  // Zoom controls
  function zoomIn() {
    setScale(prev => Math.min(prev + 0.2, 3.0));
  }

  function zoomOut() {
    setScale(prev => Math.max(prev - 0.2, 0.5));
  }

  // Apply highlight to text in PDF (can be called multiple times safely)
  function applyHighlightToText(searchText, citationIndex) {
    if (!searchText || searchText.length < 3) {
      return;
    }

    try {
      // DON'T remove ANY highlights - just add/update them
      // This function is idempotent and can be called repeatedly

      // Find and highlight new text
      const textLayers = window.document.querySelectorAll('.react-pdf__Page__textContent');
      const color = getHighlightColor(citationIndex);
      let highlightCount = 0;

      console.log(`🔍 Searching for: "${searchText.substring(0, 100)}..." in ${textLayers.length} text layers`);

      // Normalize and split search text into significant words
      const normalizeText = (text) => text.toLowerCase().replace(/[^\w\s]/g, ' ').replace(/\s+/g, ' ').trim();
      const searchNormalized = normalizeText(searchText);
      const searchWords = searchNormalized.split(/\s+/).filter(w => w.length > 3); // Only words with 4+ chars

      // Use first 15 words for more precise matching
      const keyWords = searchWords.slice(0, 15);

      console.log(`🔎 Key search words (${keyWords.length}):`, keyWords);

      // Try to find the text in each layer
      textLayers.forEach((layer, layerIndex) => {
        const textSpans = layer.querySelectorAll('span');

        // Build full text of the layer
        let fullText = '';
        textSpans.forEach(span => {
          fullText += span.textContent + ' ';
        });

        const fullTextNormalized = normalizeText(fullText);

        // Check if this layer contains enough of the search words
        const matchingWords = keyWords.filter(word => fullTextNormalized.includes(word));
        const matchPercentage = matchingWords.length / keyWords.length;

        // More strict matching - need at least 50% of key words
        if (matchPercentage >= 0.5) {
          console.log(`✅ Found ${Math.round(matchPercentage * 100)}% match in layer ${layerIndex} (${matchingWords.length}/${keyWords.length} words)`);

          // Only highlight spans that actually contain matching words
          textSpans.forEach(span => {
            const spanText = normalizeText(span.textContent);
            const hasMatch = keyWords.some(word => spanText.includes(word));

            if (hasMatch) {
              // Check if this span is already highlighted
              const alreadyHighlighted = span.classList.contains('pdf-text-highlight');

              // Always apply the highlight (this handles re-renders)
              span.classList.add('pdf-text-highlight');
              span.style.backgroundColor = color;
              span.style.borderRadius = '2px';
              span.style.padding = '2px 1px';
              span.style.boxShadow = '0 0 3px rgba(255, 255, 0, 0.5)';

              // Only add pulse animation if this is a NEW highlight (not re-applied)
              if (!alreadyHighlighted) {
                span.classList.add('pdf-highlight-pulse');

                // Remove pulse animation after it completes
                setTimeout(() => {
                  span.classList.remove('pdf-highlight-pulse');
                }, 1500);
              }

              highlightCount++;
            }
          });
        }
      });

      console.log(`✨ Highlighted ${highlightCount} text spans with mellow yellow`);

      if (highlightCount === 0) {
        console.warn(`⚠️ No matches found. Searched for ${searchWords.length} words:`, searchWords.slice(0, 5));
      }
    } catch (error) {
      console.error('Error highlighting text:', error);
    }
  }

  // Highlight text in rendered HTML (non-PDF docs) and scroll to it
  function highlightTextInHtml(searchText, citationIndex) {
    if (!searchText || searchText.length < 3 || !htmlContentRef.current) {
      return;
    }

    try {
      const container = htmlContentRef.current;

      // Clear old highlights
      container.querySelectorAll('.html-text-highlight').forEach(el => {
        const parent = el.parentNode;
        if (parent) {
          parent.replaceChild(document.createTextNode(el.textContent || ''), el);
          parent.normalize();
        }
      });

      const normalizeText = (text) => text.toLowerCase().replace(/\s+/g, ' ').trim();
      const target = normalizeText(searchText);

      const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null);
      let node = walker.nextNode();
      while (node) {
        const raw = node.nodeValue || '';
        const lower = normalizeText(raw);
        const idx = lower.indexOf(target);
        if (idx !== -1) {
          const range = document.createRange();
          range.setStart(node, Math.max(0, idx));
          range.setEnd(node, Math.min(raw.length, idx + raw.length));

          const before = raw.slice(0, idx);
          const match = raw.slice(idx, idx + searchText.length);
          const after = raw.slice(idx + searchText.length);

          const span = document.createElement('span');
          span.className = 'html-text-highlight';
          span.style.backgroundColor = getHighlightColor(citationIndex);
          span.style.borderRadius = '3px';
          span.style.padding = '2px 0';
          span.textContent = match;

          const parent = node.parentNode;
          if (parent) {
            if (before) parent.insertBefore(document.createTextNode(before), node);
            parent.insertBefore(span, node);
            if (after) parent.insertBefore(document.createTextNode(after), node);
            parent.removeChild(node);

            span.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
          }
        }
        node = walker.nextNode();
      }
    } catch (err) {
      console.warn('HTML highlight failed', err);
    }
  }

  function jumpToCitation(citation, index) {
    setActiveClaim(index);
    setActiveHighlightIndex(index);

    // Check if this is a text document (no PDF)
    if (!docData.pdf_url) {
      if (citation.supporting_text) {
        highlightTextInHtml(citation.supporting_text, index);
      }
      // Scroll to the text citation card for context
      const element = window.document.getElementById(`citation-text-${index}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        console.log(`📜 Scrolling to text citation #${index + 1}`);
      }
      return;
    }

    // For PDF documents with react-pdf
    console.log(`🎯 Jumping to PDF citation #${index + 1}:`, {
      page: citation.page_number,
      character_offset: citation.character_offset,
      text: citation.supporting_text?.substring(0, 50)
    });

    // Set search text for highlighting
    if (citation.supporting_text) {
      // Use first 20-25 words for more precise matching
      const words = citation.supporting_text.split(/\s+/);
      const searchPhrase = words.slice(0, Math.min(25, words.length)).join(' ').trim();
      setSearchText(searchPhrase);

      console.log(`🔎 Citation #${index + 1} - Will search for: "${searchPhrase.substring(0, 150)}..."`);

      // Add this highlight to the list of all highlights (for persistence)
      setAllHighlights(prev => {
        // Check if this citation is already in the list
        const exists = prev.some(h => h.citationIndex === index);
        if (exists) {
          return prev; // Already highlighted
        }
        return [...prev, { searchText: searchPhrase, citationIndex: index }];
      });

      // Apply highlight immediately
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
      highlightTimeoutRef.current = setTimeout(() => {
        applyHighlightToText(searchPhrase, index);
      }, 800);
    }

    // Determine which page to scroll to
    let targetPage = null;

    if (citation.page_number && citation.page_number > 0) {
      targetPage = citation.page_number;
    } else if (citation.character_offset) {
      // Estimate page from character offset (~2000 chars per page)
      targetPage = Math.max(1, Math.ceil(citation.character_offset / 2000));
    }

    if (targetPage) {
      setCurrentPage(targetPage);

      // Improved smooth scrolling with better timing
      const scrollToPage = () => {
        const pageElement = pageRefs.current[targetPage];
        const scrollContainer = pdfScrollContainerRef.current;

        console.log('🔍 Scroll debug:', {
          targetPage,
          hasPageElement: !!pageElement,
          hasScrollContainer: !!scrollContainer,
          scrollContainerScrollHeight: scrollContainer?.scrollHeight,
          scrollContainerClientHeight: scrollContainer?.clientHeight,
          pageElementOffsetTop: pageElement?.offsetTop,
          allPageRefs: Object.keys(pageRefs.current)
        });

        if (pageElement && scrollContainer) {
          // Calculate the actual position relative to scroll container
          const containerRect = scrollContainer.getBoundingClientRect();
          const pageRect = pageElement.getBoundingClientRect();
          const scrollTop = scrollContainer.scrollTop;
          const targetScrollTop = scrollTop + (pageRect.top - containerRect.top) - 40;

          console.log(`📐 Calculated scroll position: ${targetScrollTop} (current: ${scrollTop})`);

          // Use smooth scroll with better options
          scrollContainer.scrollTo({
            top: targetScrollTop,
            behavior: 'smooth'
          });

          console.log(`📜 Smooth scrolling to ${targetScrollTop}`);
        } else {
          console.warn('⚠️ Could not scroll - missing element or container');
        }
      };

      // Use requestAnimationFrame for smoother rendering, then scroll
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTimeout(scrollToPage, 150);
        });
      });
    }
  }

  if (loading) {
    return (
      <div style={styles.overlay}>
        <div style={styles.loadingContainer}>
          <div style={styles.spinner} />
          <div style={styles.loadingText}>Loading document from backend...</div>
          <div style={styles.loadingSubtext}>
            Fetching document {documentId} and citations
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.overlay}>
        <div style={styles.errorContainer}>
          <div style={styles.errorIcon}>⚠️</div>
          <h2 style={styles.errorTitle}>Failed to Load Document</h2>
          <p style={styles.errorMessage}>{error}</p>
          <button onClick={onClose} style={styles.closeButtonError}>
            Close
          </button>
        </div>
      </div>
    );
  }

  if (!docData) {
    return null;
  }

  return (
    <div style={styles.overlay}>
      <div style={styles.container}>
        {/* Header */}
        <div style={styles.header}>
          <button onClick={onClose} style={styles.backButton}>
            <ChevronLeft size={20} />
            <span>Back to Report</span>
          </button>
          
          <h2 style={styles.title}>Source Evidence Viewer</h2>
          
          <div style={styles.controls}>
            <button onClick={onClose} style={styles.xButton}>
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div style={styles.content}>
          {/* Left Sidebar - AI Analysis & Citations */}
          <div style={styles.sidebar}>
            <div style={styles.sidebarHeader}>
              <h3 style={styles.sidebarTitle}>AI Analysis</h3>
              <p style={styles.sidebarSubtitle}>
                Click any claim to jump to its location in the document
              </p>
            </div>

            <div style={styles.infoBox}>
              <div style={styles.infoIcon}>💡</div>
              <div style={styles.infoText}>
                <strong>Full Document Access:</strong> You're viewing the complete CMS document. 
                Click any claim to jump to that page.
              </div>
            </div>

            <div style={styles.citationsList}>
              {citations.length === 0 ? (
                <div style={styles.noCitations}>
                  No citations available for this document
                </div>
              ) : (
                citations.map((citation, idx) => (
                  <div
                    key={idx}
                    style={{
                      ...styles.citationCard,
                      ...(activeClaim === idx ? styles.citationCardActive : {}),
                      borderLeft: `4px solid ${getHighlightColor(idx).replace('0.8', '1')}`
                    }}
                    onClick={() => jumpToCitation(citation, idx)}
                  >
                    <div style={styles.citationNumber}>#{idx + 1}</div>
                    <div style={styles.citationClaim}>{citation.claim}</div>
                    
                    {citation.page_number && (
                      <div style={styles.citationMeta}>
                        📄 Page {citation.page_number}
                        {citation.section && ` • ${citation.section}`}
                      </div>
                    )}
                    
                    {citation.confidence_score && (
                      <div style={styles.confidenceBar}>
                        <div 
                          style={{
                            ...styles.confidenceFill,
                            width: `${citation.confidence_score}%`,
                            backgroundColor: 
                              citation.confidence_score > 90 ? '#10b981' :
                              citation.confidence_score > 70 ? '#f59e0b' : '#ef4444'
                          }}
                        />
                        <span style={styles.confidenceText}>
                          {citation.confidence_score}% confident
                        </span>
                      </div>
                    )}
                    
                    {citation.supporting_text && (
                      <div style={styles.citationPreview}>
                        "{citation.supporting_text.substring(0, 120)}..."
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Right Side - PDF IFRAME */}
          <div style={styles.pdfSection}>
            <div style={styles.pdfHeader}>
              <div>
                <h4 style={styles.pdfTitle}>{docData.title}</h4>
                <p style={styles.pdfMeta}>
                  {docData.date} • {citations.length} citations
                  {docData.pdf_url && docData.document_info?.pdf_url && (
                    <span style={styles.s3Badge}> • 📦 AWS S3</span>
                  )}
                </p>
              </div>
            </div>

            <div style={styles.pdfViewer}>
              {docData.pdf_url ? (
            <div style={styles.pdfContainer} ref={pdfContainerRef}>
                  {/* Zoom Controls */}
                  <div style={styles.pdfControls}>
                    <button onClick={zoomOut} style={styles.zoomButton} title="Zoom Out">
                      <ZoomOut size={18} />
                    </button>
                    <span style={styles.zoomLevel}>{Math.round(scale * 100)}%</span>
                    <button onClick={zoomIn} style={styles.zoomButton} title="Zoom In">
                      <ZoomIn size={18} />
                    </button>
                    {numPages && (
                      <span style={styles.pageInfo}>
                        Page {currentPage} of {numPages}
                      </span>
                    )}
                  </div>

                  {/* PDF Document */}
                  <div style={styles.pdfScrollContainer} ref={pdfScrollContainerRef}>
                    <Document
                      file={docData.pdf_url}
                      onLoadSuccess={onDocumentLoadSuccess}
                      onLoadError={onDocumentLoadError}
                      loading={
                        <div style={styles.pdfLoading}>
                          <div style={styles.spinner} />
                          <p>Loading PDF...</p>
                        </div>
                      }
                    >
                      {numPages && Array.from(new Array(numPages), (el, index) => (
                        <div
                          key={`page_${index + 1}`}
                          ref={el => pageRefs.current[index + 1] = el}
                          style={styles.pageWrapper}
                        >
                          <Page
                            pageNumber={index + 1}
                            scale={scale}
                            renderTextLayer={true}
                            renderAnnotationLayer={true}
                          />
                          <div style={styles.pageNumber}>Page {index + 1}</div>
                        </div>
                      ))}
                    </Document>
                  </div>
                </div>
              ) : docData.html_url ? (
                // Render HTML source inline (non-PDF) so we can highlight and scroll
                <div style={styles.htmlContainer}>
                  <div style={styles.htmlHeader}>
                    <div style={styles.textDocumentIcon}>🔗</div>
                    <div>
                      <h3 style={styles.textDocumentTitle}>Source Document (HTML)</h3>
                      <p style={styles.textDocumentSubtitle}>
                        Loaded directly from {docData.html_url}
                      </p>
                    </div>
                    <button
                      onClick={() => window.open(docData.html_url, '_blank')}
                      style={styles.openPdfButton}
                    >
                      Open in new tab ↗
                    </button>
                  </div>
                  <div style={styles.htmlContentWrapper} ref={htmlContentRef}>
                    {htmlLoading ? (
                      <div style={styles.pdfLoading}>
                        <div style={styles.spinner} />
                        <p>Loading HTML...</p>
                      </div>
                    ) : htmlContent ? (
                      <div
                        style={styles.htmlContent}
                        dangerouslySetInnerHTML={{ __html: htmlContent }}
                      />
                    ) : (
                      <div style={styles.noTextContent}>
                        <p>Unable to load HTML content.</p>
                        <button
                          onClick={() => window.open(docData.html_url, '_blank')}
                          style={styles.openPdfButton}
                        >
                          Open Source
                        </button>
                      </div>
                    )}
                  </div>
                  <div style={styles.textDocumentContent}>
                    {citations.length > 0 ? (
                      citations.map((citation, idx) => (
                        <div
                          key={idx}
                          id={`citation-text-${idx}`}
                          style={{
                            ...styles.textCitationBlock,
                            ...(activeClaim === idx ? styles.textCitationBlockActive : {})
                          }}
                        >
                          <div style={styles.textCitationHeader}>
                            <span style={{
                              ...styles.textCitationNumber,
                              backgroundColor: getHighlightColor(idx)
                            }}>
                              #{idx + 1}
                            </span>
                            <span style={styles.textCitationType}>{citation.claim_type}</span>
                          </div>

                          {citation.context && (
                            <div style={styles.textContext}>
                              {citation.context}
                            </div>
                          )}

                          <div style={{
                            ...styles.textSupporting,
                            backgroundColor: getHighlightColor(idx).replace('0.8', '0.2')
                          }}>
                            <strong>Supporting Evidence:</strong><br />
                            {citation.supporting_text}
                          </div>

                          {citation.section && (
                            <div style={styles.textSection}>
                              📍 Section: {citation.section}
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <div style={styles.noTextContent}>
                        <p>No citations available for this document.</p>
                        <button
                          onClick={() => window.open(docData.html_url, '_blank')}
                          style={styles.openPdfButton}
                        >
                          Open Source
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                // Text-based document viewer for CMS Newsroom articles
                <div style={styles.textDocumentContainer}>
                  <div style={styles.textDocumentHeader}>
                    <div style={styles.textDocumentIcon}>📰</div>
                    <div>
                      <h3 style={styles.textDocumentTitle}>CMS Newsroom Article</h3>
                      <p style={styles.textDocumentSubtitle}>
                        Text extracted from {docData.url}
                      </p>
                    </div>
                  </div>

                  <div style={styles.textDocumentContent}>
                    {citations.length > 0 ? (
                      citations.map((citation, idx) => (
                        <div
                          key={idx}
                          id={`citation-text-${idx}`}
                          style={{
                            ...styles.textCitationBlock,
                            ...(activeClaim === idx ? styles.textCitationBlockActive : {})
                          }}
                        >
                          <div style={styles.textCitationHeader}>
                            <span style={{
                              ...styles.textCitationNumber,
                              backgroundColor: getHighlightColor(idx)
                            }}>
                              #{idx + 1}
                            </span>
                            <span style={styles.textCitationType}>{citation.claim_type}</span>
                          </div>

                          {citation.context && (
                            <div style={styles.textContext}>
                              {citation.context}
                            </div>
                          )}

                          <div style={{
                            ...styles.textSupporting,
                            backgroundColor: getHighlightColor(idx).replace('0.8', '0.2')
                          }}>
                            <strong>Supporting Evidence:</strong><br />
                            {citation.supporting_text}
                          </div>

                          {citation.section && (
                            <div style={styles.textSection}>
                              📍 Section: {citation.section}
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <div style={styles.noTextContent}>
                        <p>No text content available for this document.</p>
                        {docData.url && (
                          <button
                            onClick={() => window.open(docData.url, '_blank')}
                            style={styles.openPdfButton}
                          >
                            Open Original CMS Document
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Global Styles */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        @keyframes highlightPulse {
          0% {
            box-shadow: 0 0 0 rgba(255, 255, 0, 0);
          }
          50% {
            box-shadow: 0 0 10px rgba(255, 255, 0, 0.8);
          }
          100% {
            box-shadow: 0 0 3px rgba(255, 255, 0, 0.5);
          }
        }

        .pdf-highlight-pulse {
          animation: highlightPulse 1s ease-in-out;
        }

        .pdf-text-highlight {
          transition: background-color 0.3s ease, box-shadow 0.3s ease;
        }

        /* Smooth scrolling for PDF container */
        *::-webkit-scrollbar {
          width: 12px;
        }

        *::-webkit-scrollbar-track {
          background: #f1f1f1;
        }

        *::-webkit-scrollbar-thumb {
          background: #888;
          border-radius: 6px;
        }

        *::-webkit-scrollbar-thumb:hover {
          background: #555;
        }
      `}</style>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'white',
    zIndex: 9999,
    display: 'flex',
    flexDirection: 'column',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    gap: '16px',
  },
  spinner: {
    width: '48px',
    height: '48px',
    border: '4px solid #e2e8f0',
    borderTop: '4px solid #3b82f6',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#1e293b',
  },
  loadingSubtext: {
    fontSize: '14px',
    color: '#64748b',
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    gap: '16px',
    padding: '40px',
  },
  errorIcon: {
    fontSize: '48px',
  },
  errorTitle: {
    margin: 0,
    fontSize: '24px',
    fontWeight: '700',
    color: '#1e293b',
  },
  errorMessage: {
    margin: 0,
    fontSize: '14px',
    color: '#64748b',
    textAlign: 'center',
    maxWidth: '500px',
  },
  closeButtonError: {
    padding: '12px 24px',
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
  },
  container: {
    width: '100%',
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#f8fafc',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 24px',
    backgroundColor: 'white',
    borderBottom: '1px solid #e2e8f0',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  },
  backButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 16px',
    backgroundColor: 'transparent',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    color: '#475569',
  },
  title: {
    margin: 0,
    fontSize: '20px',
    fontWeight: '600',
    color: '#1e293b',
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  xButton: {
    padding: '8px',
    backgroundColor: 'white',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
  },
  content: {
    flex: 1,
    display: 'flex',
    overflow: 'hidden',
  },
  sidebar: {
    width: '380px',
    backgroundColor: 'white',
    borderRight: '1px solid #e2e8f0',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  sidebarHeader: {
    padding: '24px',
    borderBottom: '1px solid #e2e8f0',
  },
  sidebarTitle: {
    margin: '0 0 8px 0',
    fontSize: '18px',
    fontWeight: '700',
    color: '#1e293b',
  },
  sidebarSubtitle: {
    margin: 0,
    fontSize: '13px',
    color: '#64748b',
    lineHeight: '1.5',
  },
  infoBox: {
    margin: '16px',
    padding: '16px',
    backgroundColor: '#fef3c7',
    border: '1px solid #fbbf24',
    borderRadius: '8px',
    display: 'flex',
    gap: '12px',
  },
  infoIcon: {
    fontSize: '24px',
    flexShrink: 0,
  },
  infoText: {
    fontSize: '13px',
    color: '#78350f',
    lineHeight: '1.5',
  },
  citationsList: {
    flex: 1,
    overflowY: 'auto',
    padding: '16px',
  },
  noCitations: {
    padding: '40px 20px',
    textAlign: 'center',
    color: '#94a3b8',
    fontSize: '14px',
  },
  citationCard: {
    backgroundColor: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    padding: '16px',
    marginBottom: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    position: 'relative',
  },
  citationCardActive: {
    backgroundColor: '#f1f5f9',
    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
    transform: 'translateX(4px)',
  },
  citationNumber: {
    position: 'absolute',
    top: '12px',
    right: '12px',
    backgroundColor: '#f1f5f9',
    color: '#64748b',
    fontSize: '12px',
    fontWeight: '700',
    padding: '4px 10px',
    borderRadius: '12px',
  },
  citationClaim: {
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: '10px',
    fontSize: '14px',
    lineHeight: '1.5',
    paddingRight: '40px',
  },
  citationMeta: {
    fontSize: '12px',
    color: '#64748b',
    marginBottom: '10px',
    fontWeight: '500',
  },
  confidenceBar: {
    height: '6px',
    backgroundColor: '#e2e8f0',
    borderRadius: '3px',
    overflow: 'hidden',
    position: 'relative',
    marginBottom: '10px',
  },
  confidenceFill: {
    height: '100%',
    transition: 'width 0.5s ease',
    borderRadius: '3px',
  },
  confidenceText: {
    position: 'absolute',
    right: '4px',
    top: '-18px',
    fontSize: '11px',
    color: '#64748b',
    fontWeight: '600',
  },
  citationPreview: {
    fontSize: '12px',
    color: '#94a3b8',
    fontStyle: 'italic',
    lineHeight: '1.5',
    marginTop: '8px',
    paddingTop: '8px',
    borderTop: '1px solid #f1f5f9',
  },
  pdfSection: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#f8fafc',
  },
  pdfHeader: {
    padding: '20px 32px',
    backgroundColor: 'white',
    borderBottom: '1px solid #e2e8f0',
  },
  pdfTitle: {
    margin: '0 0 4px 0',
    fontSize: '16px',
    fontWeight: '600',
    color: '#1e293b',
  },
  pdfMeta: {
    margin: 0,
    fontSize: '13px',
    color: '#64748b',
  },
  s3Badge: {
    color: '#10b981',
    fontWeight: '600',
  },
  pdfViewer: {
    flex: 1,
    padding: '12px',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  pdfContainer: {
    width: '100%',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
    overflow: 'hidden',
  },
  pdfControls: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    backgroundColor: '#f8fafc',
    borderBottom: '1px solid #e2e8f0',
  },
  zoomButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    border: '1px solid #cbd5e1',
    borderRadius: '6px',
    backgroundColor: 'white',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  zoomLevel: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#475569',
    minWidth: '50px',
    textAlign: 'center',
  },
  pageInfo: {
    marginLeft: 'auto',
    fontSize: '14px',
    color: '#64748b',
  },
  pdfScrollContainer: {
    flex: 1,
    overflowY: 'scroll',
    overflowX: 'auto',
    backgroundColor: '#e2e8f0',
    padding: '12px',
    maxHeight: '100%',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    scrollBehavior: 'smooth',
    WebkitOverflowScrolling: 'touch',
  },
  pageWrapper: {
    marginBottom: '16px',
    backgroundColor: 'white',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    position: 'relative',
    width: '100%',
    maxWidth: '100%',
  },
  pageNumber: {
    position: 'absolute',
    bottom: '8px',
    right: '8px',
    backgroundColor: 'rgba(0,0,0,0.7)',
    color: 'white',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '600',
  },
  pdfLoading: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px',
    color: '#64748b',
  },
  iframe: {
    width: '100%',
    height: '100%',
    border: 'none',
    borderRadius: '8px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
    backgroundColor: 'white',
  },
  noPdf: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    padding: '40px',
    textAlign: 'center',
  },
  noPdfIcon: {
    fontSize: '64px',
    marginBottom: '16px',
    opacity: 0.5,
  },
  noPdfTitle: {
    margin: '0 0 12px 0',
    fontSize: '20px',
    fontWeight: '600',
    color: '#1e293b',
  },
  noPdfText: {
    margin: '0 0 24px 0',
    fontSize: '14px',
    color: '#64748b',
    maxWidth: '400px',
    lineHeight: '1.6',
  },
  pdfBlockedMessage: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: 'white',
    padding: '40px',
    borderRadius: '12px',
    boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
    textAlign: 'center',
    maxWidth: '500px',
    zIndex: 10,
  },
  pdfBlockedIcon: {
    fontSize: '48px',
    marginBottom: '16px',
  },
  pdfBlockedTitle: {
    margin: '0 0 12px 0',
    fontSize: '20px',
    fontWeight: '700',
    color: '#1e293b',
  },
  pdfBlockedText: {
    margin: '0 0 24px 0',
    fontSize: '14px',
    color: '#64748b',
    lineHeight: '1.6',
  },
  openPdfButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 32px',
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '15px',
    fontWeight: '600',
    boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)',
  },
  externalLinkIcon: {
    fontSize: '18px',
    fontWeight: '700',
  },
  // Text document viewer styles
  textDocumentContainer: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#f8fafc',
  },
  htmlContainer: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    overflow: 'hidden',
  },
  htmlHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px',
    backgroundColor: 'white',
    borderBottom: '1px solid #e2e8f0',
    gap: '12px',
  },
  htmlIframeWrapper: {
    flex: 1,
    backgroundColor: 'white',
    borderTop: '1px solid #e2e8f0',
  },
  htmlContentWrapper: {
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    padding: '12px',
    backgroundColor: 'white',
    maxHeight: '60vh',
    overflowY: 'auto',
    boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
  },
  htmlContent: {
    color: '#1f2937',
    lineHeight: '1.6',
  },
  htmlTextHighlight: {
    backgroundColor: 'rgba(255, 255, 102, 0.5)',
  },
  textDocumentHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '20px 24px',
    backgroundColor: 'white',
    borderBottom: '1px solid #e2e8f0',
  },
  textDocumentIcon: {
    fontSize: '32px',
  },
  textDocumentTitle: {
    margin: '0 0 4px 0',
    fontSize: '18px',
    fontWeight: '600',
    color: '#1e293b',
  },
  textDocumentSubtitle: {
    margin: 0,
    fontSize: '13px',
    color: '#64748b',
  },
  textDocumentContent: {
    flex: 1,
    overflowY: 'auto',
    padding: '24px',
  },
  textCitationBlock: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '16px',
    border: '2px solid #e2e8f0',
    transition: 'all 0.3s ease',
  },
  textCitationBlockActive: {
    border: '2px solid #3b82f6',
    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.2)',
    transform: 'scale(1.02)',
  },
  textCitationHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '12px',
  },
  textCitationNumber: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    color: 'white',
    fontSize: '14px',
    fontWeight: '700',
  },
  textCitationType: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  textContext: {
    fontSize: '14px',
    lineHeight: '1.6',
    color: '#475569',
    marginBottom: '12px',
    padding: '12px',
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    borderLeft: '3px solid #cbd5e1',
  },
  textSupporting: {
    fontSize: '14px',
    lineHeight: '1.6',
    color: '#1e293b',
    padding: '16px',
    borderRadius: '8px',
    marginBottom: '12px',
  },
  textSection: {
    fontSize: '13px',
    color: '#64748b',
    fontStyle: 'italic',
  },
  noTextContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    textAlign: 'center',
    color: '#64748b',
  },
};
